import { createFileRoute } from "@tanstack/react-router";
import { Bullets, LegalPage, Section, Value } from "@/components/legal-page";
import { LEGAL } from "@/lib/legal";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title: "Xamseen — Accessibility" },
      {
        name: "description",
        content:
          "Xamseen's accessibility commitments, what we have built in, the gaps we know about, and how to tell us when something blocks you.",
      },
      { property: "og:title", content: "Xamseen — Accessibility" },
      { property: "og:description", content: "Our accessibility commitments, current state and known gaps." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/accessibility" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/accessibility" }],
  }),
  component: AccessibilityPage,
});

const TOC = [
  { id: "commitment", label: "Our commitment" },
  { id: "built", label: "What is built in today" },
  { id: "gaps", label: "Known gaps" },
  { id: "feedback", label: "Tell us what blocks you" },
];

function AccessibilityPage() {
  return (
    <LegalPage
      title="Accessibility"
      intro="Xamseen is for whoever lives in a place, which means it has to work for people using screen readers, keyboards, magnification, and phones on bad connections. This page states where we actually are — including what is not done yet."
      toc={TOC}
    >
      <Section id="commitment" title="Our commitment">
        <p>
          We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. We have not completed a formal
          third-party audit or published a conformance statement, so we describe this as our target rather than a
          certified result. When we complete an audit, we will publish what it found here, including the failures.
        </p>
        <p>
          Accessibility is treated as part of building a feature, not a later cleanup pass. Where we fall short, we would
          rather hear about it and fix it than leave it undocumented.
        </p>
      </Section>

      <Section id="built" title="What is built in today">
        <Bullets
          items={[
            <>
              <strong>Keyboard navigation.</strong> Interactive controls are reachable and operable by keyboard, with
              visible focus indicators. Dialogs trap focus and close on Escape.
            </>,
            <>
              <strong>Screen reader support.</strong> Semantic HTML, labelled buttons and inputs, accessible names on
              icon-only controls, and headings that reflect the actual structure of each page.
            </>,
            <>
              <strong>Colour and contrast.</strong> Body text and interface colours are chosen against the WCAG AA
              contrast targets in both light and dark themes. Colour is not used as the only way to convey meaning —
              status and severity carry text labels too.
            </>,
            <>
              <strong>Light, dark and system themes.</strong> Including respecting your operating system preference, which
              matters for light sensitivity and for low-light reading.
            </>,
            <>
              <strong>Reduced motion.</strong> Animation is subtle by default, and we honour the system "reduce motion"
              preference.
            </>,
            <>
              <strong>Text and zoom.</strong> Layouts use relative units so browser zoom and larger system text sizes
              reflow rather than clip.
            </>,
            <>
              <strong>Mobile and touch.</strong> Mobile-first layouts with touch targets sized for real thumbs, and no
              horizontal scrolling traps.
            </>,
            <>
              <strong>Alternatives to precise input.</strong> You can describe a location in words instead of placing a
              precise map pin, and file a report without using the camera.
            </>,
            <>
              <strong>Low bandwidth.</strong> Images are served at appropriate sizes, content loads progressively, and the
              installed app keeps working when the connection drops.
            </>,
          ]}
        />
      </Section>

      <Section id="gaps" title="Known gaps">
        <p>Being straight about the current state:</p>
        <Bullets
          items={[
            <>
              <strong>The map.</strong> An interactive map is inherently visual. Map content is also available as a list of
              nearby reports, but the map itself is not yet fully usable by screen reader alone.
            </>,
            <>
              <strong>User-supplied media.</strong> We cannot guarantee that photos and videos posted by other people carry
              descriptive alternative text. We are working on prompting authors to describe their images.
            </>,
            <>
              <strong>No formal audit yet.</strong> Our AA claim is a target validated by internal testing, not by an
              independent assessor.
            </>,
            <>
              <strong>Translation coverage.</strong> Interface language coverage is still expanding, and community content
              appears in the language it was written in.
            </>,
          ]}
        />
      </Section>

      <Section id="feedback" title="Tell us what blocks you">
        <p>
          If any part of Xamseen is difficult or impossible for you to use, tell us — it is the fastest way for it to get
          fixed. Contact <Value value={LEGAL.supportEmail} label="general support and accessibility contact address" />.
        </p>
        <p>
          Helpful details: the page or feature, what you were trying to do, and the device, browser and assistive technology
          you were using. We treat accessibility barriers as defects and prioritise them accordingly, and we will tell you
          what we plan to do about the specific issue you raised.
        </p>
      </Section>
    </LegalPage>
  );
}
